import { useMemo } from 'react';
import styled from 'styled-components';

import { AccessModes, Dimensions, Metrics, Sources } from 'components/insights/constants';
import MetricTile from 'components/insights/layout/tiles/MetricTile';
import AreaChart from 'components/shared/charts/AreaChart';
import { getFormattedDatesInRange } from 'components/shared/charts/helpers';
import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import { useNetworkPerformanceActions, useNetworkPerformanceStore } from 'stores/networkPerformanceStore';
import ReferralIcon from 'svg/ReferralIcon';

import useNetworkPerformanceAnalytics from '../query/useNetworkPerformanceAnalytics';
import { METRIC_CONFIGS, NetworkPerformanceMetrics } from '../slideout/constants';

/** Format "2026-05-17" → "5/17" (no leading zeros, matching Figma) */
const formatShortDate = (date: string) => {
  const [, month, day] = date.split('-');
  return `${parseInt(month, 10)}/${parseInt(day, 10)}`;
};

export default function AvgStartOfCareTile() {
  const { openSlideout } = useNetworkPerformanceActions();
  const filters = useNetworkPerformanceStore((state) => state.filters);

  const queryParams = useMemo(
    () => ({
      source: Sources.INSIGHTS_CARE_TRANSITIONS,
      accessModes: [AccessModes.MANAGER],
      dimensions: [Dimensions.DATE],
      metrics: [Metrics.DAYS_TO_START_CARE_AVERAGE, Metrics.LOCATION_EPISODE_ID_COUNT],
      rollups: true,
    }),
    []
  );

  const { loading, data, rollups } = useNetworkPerformanceAnalytics(queryParams, {
    queryKey: 'networkPerformanceAvgStartOfCare',
  });

  const avgDays = rollups.overall.metrics.getCurrentDataPoint(Metrics.DAYS_TO_START_CARE_AVERAGE, 0);
  const patientVolume = rollups.overall.metrics.getCurrentDataPoint(Metrics.LOCATION_EPISODE_ID_COUNT, 0);

  const dates = getFormattedDatesInRange(filters[START_DATE] as Date, filters[END_DATE] as Date).reduce(
    (acc, formattedDate) => {
      acc[formattedDate] = null;
      return acc;
    },
    {} as Record<string, number | null>
  );

  const rawCategories = data.dimensions.getCurrentDimensionValuesByIndex(0);
  const rawValues = data.metrics.getCurrentSeries(Metrics.DAYS_TO_START_CARE_AVERAGE);
  rawCategories.forEach((date, idx) => {
    dates[date] = rawValues[idx] ?? null;
  });

  const categories = Object.keys(dates).map(formatShortDate);
  const values = Object.values(dates);

  const hasData = avgDays != null;

  return (
    <MetricTile
      label={METRIC_CONFIGS[NetworkPerformanceMetrics.AVG_START_OF_CARE].label}
      value={hasData ? `${avgDays.toFixed(1)} days` : undefined}
      loading={loading}
      hasData={hasData}
      onClick={() => openSlideout(NetworkPerformanceMetrics.AVG_START_OF_CARE)}>
      <AreaChart
        categories={categories}
        values={values as (number | null)[]}
        formatTooltipSupertext={(x) => String(x)}
        formatTooltipSubtext={(y) => (y != null ? `${y.toFixed(1)} days` : undefined)}
        config={{
          chart: { height: 135, spacing: [4, 4, 4, 4] },
          plotOptions: {
            area: {
              connectNulls: true,
            },
          },
          yAxis: {
            labels: {
              format: '{value:.1f}',
              style: { fontSize: '9px' },
            },
          },
          xAxis: {
            labels: {
              style: { fontSize: '9px' },
            },
          },
        }}
      />
      <Footer>
        <FooterLeft>
          <ReferralIcon width={12} height={12} />
          <FooterLabel>Patient Volume</FooterLabel>
        </FooterLeft>
        <FooterValue>{patientVolume ?? 0}</FooterValue>
      </Footer>
    </MetricTile>
  );
}

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FooterLabel = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.black};
`;

const FooterValue = styled.span`
  font-size: 11px;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.black};
`;
